import { useEffect, useRef, useState } from "react";
import { createChart, CandlestickSeries, LineSeries, HistogramSeries } from "lightweight-charts";
import { Button } from "@/components/ui/button";
import { BarChart3, Activity, TrendingUp, Settings2 } from "lucide-react";
import { rsiBgColor } from "@/lib/signalColors";

export default function CandlestickChart({ symbol, ohlcData, indicators, onTimeframeChange, currentDays, onParamsChange }) {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const [activeIndicator, setActiveIndicator] = useState("none");
  const [showParams, setShowParams] = useState(false);
  const [params, setParams] = useState({
    rsi_period: 14,
    macd_fast: 12,
    macd_slow: 26,
    macd_signal: 9,
    bb_period: 20,
    bb_std: 2.0
  });

  const handleParamChange = (key, value) => {
    const newParams = { ...params, [key]: value };
    setParams(newParams);
    if (onParamsChange) onParamsChange(newParams);
  };

  useEffect(() => {
    if (!chartContainerRef.current || !ohlcData || ohlcData.length === 0) return;

    // Clean up previous chart
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { color: 'transparent' },
        textColor: '#64748B',
        fontSize: 11,
      },
      localization: {
        locale: 'en-US',
        dateFormat: 'yyyy-MM-dd',
      },
      grid: {
        vertLines: { color: 'rgba(255,255,255,0.03)' },
        horzLines: { color: 'rgba(255,255,255,0.03)' },
      },
      crosshair: {
        mode: 0,
        vertLine: { color: 'rgba(0,224,255,0.3)', width: 1, style: 2 },
        horzLine: { color: 'rgba(0,224,255,0.3)', width: 1, style: 2 },
      },
      rightPriceScale: {
        borderColor: 'rgba(255,255,255,0.05)',
        scaleMargins: { top: 0.1, bottom: 0.2 },
      },
      timeScale: {
        borderColor: 'rgba(255,255,255,0.05)',
        timeVisible: false,
        tickMarkFormatter: (time) => {
          if (typeof time === 'string') {
            const parts = time.split('-');
            const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            return `${months[parseInt(parts[1], 10) - 1]} ${parseInt(parts[2], 10)}`;
          }
          return String(time);
        },
      },
      width: chartContainerRef.current.clientWidth,
      height: 360,
    });

    // Candlestick series (v5 API)
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#00FFA3',
      downColor: '#FF3B30',
      borderUpColor: '#00FFA3',
      borderDownColor: '#FF3B30',
      wickUpColor: '#00FFA3',
      wickDownColor: '#FF3B30',
    });

    candleSeries.setData(ohlcData);

    // Add Bollinger Bands if active
    if (activeIndicator === "bollinger" && indicators) {
      const bbUpper = indicators.filter(d => d.bb_upper !== null).map(d => ({ time: d.time, value: d.bb_upper }));
      const bbMiddle = indicators.filter(d => d.bb_middle !== null).map(d => ({ time: d.time, value: d.bb_middle }));
      const bbLower = indicators.filter(d => d.bb_lower !== null).map(d => ({ time: d.time, value: d.bb_lower }));

      if (bbUpper.length > 0) {
        const upperSeries = chart.addSeries(LineSeries, { color: 'rgba(99,102,241,0.5)', lineWidth: 1, lineStyle: 2 });
        upperSeries.setData(bbUpper);

        const middleSeries = chart.addSeries(LineSeries, { color: 'rgba(99,102,241,0.8)', lineWidth: 1 });
        middleSeries.setData(bbMiddle);

        const lowerSeries = chart.addSeries(LineSeries, { color: 'rgba(99,102,241,0.5)', lineWidth: 1, lineStyle: 2 });
        lowerSeries.setData(bbLower);
      }
    }

    // Volume
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
    });

    chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.85, bottom: 0 },
    });

    volumeSeries.setData(ohlcData.map(d => ({
      time: d.time,
      value: d.volume,
      color: d.close >= d.open ? 'rgba(0,255,163,0.15)' : 'rgba(255,59,48,0.15)',
    })));

    chart.timeScale().fitContent();
    chartRef.current = chart;

    // Resize observer
    const resizeObserver = new ResizeObserver(entries => {
      if (entries.length > 0) {
        const { width } = entries[0].contentRect;
        chart.applyOptions({ width });
      }
    });
    resizeObserver.observe(chartContainerRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.remove();
      chartRef.current = null;
    };
  }, [ohlcData, activeIndicator, indicators]);

  // Filtered indicator data for sub-charts
  const rsiData = indicators?.filter(d => d.rsi !== null).map(d => ({ time: d.time, rsi: d.rsi })) || [];
  const macdData = indicators?.filter(d => d.macd !== null).map(d => ({ time: d.time, macd: d.macd, signal: d.macd_signal, histogram: d.macd_histogram })) || [];

  const timeframes = [
    { days: 7, label: "1W" },
    { days: 30, label: "1M" },
    { days: 90, label: "3M" },
    { days: 180, label: "6M" },
    { days: 365, label: "1Y" },
  ];

  const indicatorOptions = [
    { key: "none", label: "Price Only", icon: BarChart3 },
    { key: "bollinger", label: "Bollinger", icon: TrendingUp },
    { key: "rsi", label: "RSI", icon: Activity },
    { key: "macd", label: "MACD", icon: Activity },
  ];

  return (
    <div data-testid="candlestick-chart" className="space-y-3">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1">
          {timeframes.map(tf => (
            <Button
              key={tf.days}
              data-testid={`chart-tf-${tf.label}`}
              size="sm"
              variant={currentDays === tf.days ? "default" : "outline"}
              onClick={() => onTimeframeChange(tf.days)}
              className={`text-xs px-2 py-1 h-7 ${currentDays === tf.days
                ? 'bg-[#00E0FF]/20 text-[#00E0FF] border-[#00E0FF]/30'
                : 'border-white/10 text-gray-400 hover:text-white'}`}
            >
              {tf.label}
            </Button>
          ))}
        </div>
        <div className="flex gap-1">
          {indicatorOptions.map(opt => (
            <Button
              key={opt.key}
              data-testid={`indicator-${opt.key}`}
              size="sm"
              variant={activeIndicator === opt.key ? "default" : "outline"}
              onClick={() => setActiveIndicator(opt.key)}
              className={`text-xs px-2 py-1 h-7 ${activeIndicator === opt.key
                ? 'bg-[#6366F1]/20 text-[#6366F1] border-[#6366F1]/30'
                : 'border-white/10 text-gray-400 hover:text-white'}`}
            >
              {opt.label}
            </Button>
          ))}
          <Button
            data-testid="indicator-settings-btn"
            size="sm"
            variant="outline"
            onClick={() => setShowParams(!showParams)}
            className={`text-xs px-2 py-1 h-7 ${showParams ? 'bg-white/10 text-white' : 'border-white/10 text-gray-400 hover:text-white'}`}
          >
            <Settings2 className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Custom Parameters Panel */}
      {showParams && (
        <div data-testid="params-panel" className="glass-effect p-3 rounded-lg border border-white/5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
            {activeIndicator === "rsi" || activeIndicator === "none" ? (
              <div>
                <label className="text-gray-500 block mb-1">RSI Period</label>
                <input
                  data-testid="param-rsi-period"
                  type="number" min="2" max="50" value={params.rsi_period}
                  onChange={(e) => handleParamChange("rsi_period", parseInt(e.target.value) || 14)}
                  className="w-full px-2 py-1.5 bg-black/30 border border-white/10 rounded text-white text-xs"
                />
              </div>
            ) : null}
            {activeIndicator === "macd" || activeIndicator === "none" ? (
              <>
                <div>
                  <label className="text-gray-500 block mb-1">MACD Fast</label>
                  <input
                    data-testid="param-macd-fast"
                    type="number" min="2" max="50" value={params.macd_fast}
                    onChange={(e) => handleParamChange("macd_fast", parseInt(e.target.value) || 12)}
                    className="w-full px-2 py-1.5 bg-black/30 border border-white/10 rounded text-white text-xs"
                  />
                </div>
                <div>
                  <label className="text-gray-500 block mb-1">MACD Slow</label>
                  <input
                    data-testid="param-macd-slow"
                    type="number" min="2" max="100" value={params.macd_slow}
                    onChange={(e) => handleParamChange("macd_slow", parseInt(e.target.value) || 26)}
                    className="w-full px-2 py-1.5 bg-black/30 border border-white/10 rounded text-white text-xs"
                  />
                </div>
                <div>
                  <label className="text-gray-500 block mb-1">MACD Signal</label>
                  <input
                    data-testid="param-macd-signal"
                    type="number" min="2" max="50" value={params.macd_signal}
                    onChange={(e) => handleParamChange("macd_signal", parseInt(e.target.value) || 9)}
                    className="w-full px-2 py-1.5 bg-black/30 border border-white/10 rounded text-white text-xs"
                  />
                </div>
              </>
            ) : null}
            {activeIndicator === "bollinger" || activeIndicator === "none" ? (
              <>
                <div>
                  <label className="text-gray-500 block mb-1">BB Period</label>
                  <input
                    data-testid="param-bb-period"
                    type="number" min="5" max="50" value={params.bb_period}
                    onChange={(e) => handleParamChange("bb_period", parseInt(e.target.value) || 20)}
                    className="w-full px-2 py-1.5 bg-black/30 border border-white/10 rounded text-white text-xs"
                  />
                </div>
                <div>
                  <label className="text-gray-500 block mb-1">BB Std Dev</label>
                  <input
                    data-testid="param-bb-std"
                    type="number" min="0.5" max="4" step="0.1" value={params.bb_std}
                    onChange={(e) => handleParamChange("bb_std", parseFloat(e.target.value) || 2.0)}
                    className="w-full px-2 py-1.5 bg-black/30 border border-white/10 rounded text-white text-xs"
                  />
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Main Candlestick Chart */}
      <div ref={chartContainerRef} className="w-full rounded-lg overflow-hidden" />

      {/* RSI Sub-chart */}
      {activeIndicator === "rsi" && rsiData.length > 0 && (
        <div data-testid="rsi-chart" className="glass-effect p-3 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-[#6366F1] uppercase tracking-wider">RSI (14)</span>
            <div className="flex items-center gap-3 text-[10px] text-gray-400">
              <span>Overbought: <span className="text-[#FF3B30]">70</span></span>
              <span>Oversold: <span className="text-[#00FFA3]">30</span></span>
            </div>
          </div>
          <div className="flex items-end gap-[1px] h-16">
            {rsiData.slice(-60).map((d) => (
              <div
                key={d.time}
                className="flex-1 min-w-[2px] rounded-t-sm"
                style={{
                  height: `${d.rsi}%`,
                  backgroundColor: rsiBgColor(d.rsi),
                  opacity: 0.7,
                }}
              />
            ))}
          </div>
          <div className="flex justify-between mt-1 text-[10px] text-gray-500">
            <span>{rsiData.length > 0 ? rsiData[rsiData.length - 1].time : ''}</span>
            <span className={`font-bold ${
              rsiData[rsiData.length - 1]?.rsi > 70 ? 'text-[#FF3B30]' :
              rsiData[rsiData.length - 1]?.rsi < 30 ? 'text-[#00FFA3]' : 'text-[#6366F1]'
            }`}>
              RSI: {rsiData[rsiData.length - 1]?.rsi}
            </span>
          </div>
        </div>
      )}

      {/* MACD Sub-chart */}
      {activeIndicator === "macd" && macdData.length > 0 && (
        <div data-testid="macd-chart" className="glass-effect p-3 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-[#F59E0B] uppercase tracking-wider">MACD (12,26,9)</span>
            <div className="flex items-center gap-3 text-[10px]">
              <span className="text-[#00E0FF]">MACD: {macdData[macdData.length - 1]?.macd?.toFixed(2)}</span>
              <span className="text-[#F59E0B]">Signal: {macdData[macdData.length - 1]?.signal?.toFixed(2)}</span>
            </div>
          </div>
          <div className="flex items-center gap-[1px] h-16">
            {macdData.slice(-60).map((d) => {
              const h = d.histogram || 0;
              const maxH = Math.max(...macdData.slice(-60).map(x => Math.abs(x.histogram || 0)));
              const pct = maxH > 0 ? Math.abs(h) / maxH * 50 : 0;
              return (
                <div key={d.time} className="flex-1 min-w-[2px] flex flex-col justify-center h-full">
                  {h >= 0 ? (
                    <div className="bg-[#00FFA3]/60 rounded-t-sm self-stretch" style={{ height: `${pct}%` }} />
                  ) : (
                    <div className="bg-[#FF3B30]/60 rounded-b-sm self-stretch mt-auto" style={{ height: `${pct}%` }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
